import React, { useState, useRef } from 'react';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { BsPlusLg } from 'react-icons/bs';
import { TbLayoutDashboard } from "react-icons/tb";
import { ChatItem } from '../types';
import RenameChatDialog from './RenameChatDialog';
import DeleteChatDialog from './DeleteChatDialog';
import ChatHistoryList from './ChatHistoryList';
import { updateChatTitle, deleteChat } from '../utils/chatApi';
import { useDebouncedLoading } from '../hooks/useDebouncedLoading';
import { useAuth } from '../../../contexts/AuthContext';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  onNewChat?: () => void;
  // onOpenDashboard?: () => void;
  chats?: ChatItem[];
  unreadChatIds?: Set<string>;
  isLoadingChats?: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  onChatSelect?: (chatId: string) => void;
  selectedChatId?: string | null;
  onChatUpdate?: () => void;
  onLoadMore?: () => void;
  onChatDeleted?: (chatId: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onToggle,
  onNewChat,
  // onOpenDashboard,
  chats = [],
  unreadChatIds,
  isLoadingChats = false,
  isLoadingMore = false,
  hasMore = false,
  onChatSelect,
  selectedChatId,
  onChatUpdate,
  onLoadMore,
  onChatDeleted
}) => {
  const [renamingChat, setRenamingChat] = useState<ChatItem | null>(null);
  const [deletingChat, setDeletingChat] = useState<ChatItem | null>(null);
  const [isDeletingChat, setIsDeletingChat] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const showLoading = useDebouncedLoading(isLoadingChats, 500);
  const { isAdmin } = useAuth();

  const handleRenameChat = (chat: ChatItem) => {
    setRenamingChat(chat);
  };

  const handleRenameSubmit = async (newTitle: string) => {
    if (!renamingChat) return;

    try {
      await updateChatTitle(renamingChat.chat_id, newTitle);
      setRenamingChat(null);
      onChatUpdate?.();
    } catch (error) {
      console.error('Error updating chat title:', error);
      // TODO: Show error message to user
    }
  };

  const handleRenameCancel = () => {
    setRenamingChat(null);
  };

  const handleDeleteRequest = (chat: ChatItem) => {
    setDeletingChat(chat);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingChat) return;

    setIsDeletingChat(true);
    setDeleteError(null);

    try {
      await deleteChat(deletingChat.chat_id);
      onChatUpdate?.();
      onChatDeleted?.(deletingChat.chat_id);
      setDeletingChat(null);
    } catch (error) {
      console.error('Error deleting chat:', error);
      const message =
        error instanceof Error ? error.message : 'Unable to delete chat.';
      setDeleteError(message);
    } finally {
      setIsDeletingChat(false);
    }
  };

  const handleDeleteCancel = () => {
    if (isDeletingChat) return;
    setDeletingChat(null);
    setDeleteError(null);
  };


  return (
    <div
      className={`h-full bg-white border-r border-gray-200 transition-all duration-300 flex flex-col relative ${
        isOpen ? 'w-72' : 'w-24'
      }`}
    >
      {/* Toggle button - positioned absolute in the top right */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-4 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-600 hover:text-gray-900 shadow-sm transition-colors z-10"
      >
        {isOpen ? (
          <IoChevronBack className="w-4.5 h-4.5" />
        ) : (
          <IoChevronForward className="w-4.5 h-4.5" />
        )}
      </button>

      {/* Dashboard button - only visible to admins */}
      {/* {isAdmin && (
        <div className="h-16 flex items-center px-4 border-b border-gray-100">
          <button
            type="button"
            onClick={onOpenDashboard}
            className="w-full h-8 rounded-md flex items-center justify-center gap-2 text-sm text-gray-600 border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <TbLayoutDashboard className="w-4 h-4" />
            {isOpen && <span>Dashboard</span>}
          </button>
        </div>
      )} */}

      {/* New chat button */}
      <div className="p-4">
        <button
          onClick={onNewChat}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <BsPlusLg className="w-4 h-4" />
          {isOpen && <span>New Chat</span>}
        </button>
      </div>

      {/* Chat history */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-3">
        {isOpen && (
          <ChatHistoryList
            chats={chats}
            unreadChatIds={unreadChatIds}
            isLoading={showLoading}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            selectedChatId={selectedChatId}
            onChatSelect={onChatSelect || (() => {})}
            onChatRename={handleRenameChat}
            onChatDeleteRequest={handleDeleteRequest}
            onLoadMore={onLoadMore}
            scrollContainerRef={scrollContainerRef}
          />
        )}
      </div>

      {/* Rename Chat Dialog */}
      {renamingChat && (
        <RenameChatDialog
          currentTitle={renamingChat.title}
          onSubmit={handleRenameSubmit}
          onCancel={handleRenameCancel}
        />
      )}

      {/* Delete Chat Dialog */}
      {deletingChat && (
        <DeleteChatDialog
          chatTitle={deletingChat.title}
          isDeleting={isDeletingChat}
          errorMessage={deleteError}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}
    </div>
  );
};

export default Sidebar;
